import { useState, type ReactNode } from 'react'
import { formatMD, formatMDHM } from '@indipia/shared'
import { Badge, EmptyState, ErrorState, Modal, Skeleton, Table, TextLink, type TableColumn } from '@indipia/ui-core'
import type { OrderItemView, OrderSendHistoryItem, OrderVendorRow } from '../../api/orderApi'
import {
  CLOSED_REASON_LABEL, DECLINE_REASON_LABEL, ORDER_MESSAGE, QTY_CHECK_LABEL, RECEIPT_METHOD_LABEL,
  replyElapsedText, SEND_METHOD_LABEL, SEND_STATUS_LABEL, type MaterialStepKey,
} from '../../constants/materialOrder.logic'
import { useOrderSends } from '../../hooks/useOrderSends'
import { ShipPhotoViewerHost } from '../ship/ShipPhotoViewerHost'

// 자재 세부 내역 팝업 5종(B-11) — 진행 요약 단계 줄을 누르면 해당 단계 내역을 보여 준다.
//  ① 발주정보 요청(발송 내용·품목표) ② 발주정보 회신 ③ 배송정보 요청 ④ 배송정보 회신(배송정보·차량·기사 연락처·상차사진) ⑤ 입고 내역(하차사진·수량 부족·파손)
// 원천: 원장 행(order-vendors — delivery·receipt 포함) + 발송·회신 이력(order-sends) + 사진(delivery-photos, PhotoViewer).
// 등록자·회신자는 ID 만 내려와 이름을 알 수 없으므로 표시하지 않는다. 값이 없는 줄은 생략(채우지 않음).

interface Props {
  open: boolean
  onClose: () => void
  row: OrderVendorRow
  stepKey: MaterialStepKey
  /** 머리 부제의 공종명(없으면 거래처명만) */
  workTypeName?: string
}

const TITLE: Record<MaterialStepKey, string> = {
  'order-request': ORDER_MESSAGE.stepTitles[0],
  'order-reply': ORDER_MESSAGE.stepTitles[1],
  'ship-request': ORDER_MESSAGE.stepTitles[2],
  'ship-reply': ORDER_MESSAGE.stepTitles[3],
  receipt: ORDER_MESSAGE.stepTitles[4],
}

const SEND_TYPE_LABEL: Record<string, string> = {
  ORDER_REQUEST: '발주 요청', ORDER_CHANGE: '발주 변경 요청', SHIP_REQUEST: '배송정보 요청',
}

const caption: React.CSSProperties = { fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }
const cardStyle: React.CSSProperties = { border: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-surface)' }

/** 항목 한 줄(라벨 + 값) — 값이 비면 그리지 않는다 */
function Line({ label, children }: { label: string; children: ReactNode }) {
  if (children == null || children === '' || children === false) return null
  return (
    <div className="flex gap-2 text-xs" style={{ color: 'var(--shell-on-surface)' }}>
      <span className="shrink-0" style={{ ...caption, minWidth: 72 }}>{label}</span>
      <span className="min-w-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</span>
    </div>
  )
}

function md(v: string | null | undefined): string { return formatMD(v) ?? '' }
function mdhm(v: string | null | undefined): string { return formatMDHM(v) ?? '' }

function SendCard({ s }: { s: OrderSendHistoryItem }) {
  const sent = mdhm(s.sentDtm)
  const delivery = s.oldDeliveryDate && s.deliveryDate && s.oldDeliveryDate !== s.deliveryDate
    ? `${md(s.oldDeliveryDate)} → ${md(s.deliveryDate)}`
    : md(s.deliveryDate)
  return (
    <li className="rounded-xl p-3 flex flex-col gap-1" style={cardStyle}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <b className="text-sm" style={{ color: 'var(--shell-on-surface)' }}>{SEND_TYPE_LABEL[s.sendTypeCode] ?? s.sendTypeCode}</b>
        {s.attemptNo != null && <span style={caption}>{`${s.attemptNo}차`}</span>}
        {s.sendStatus && <Badge status={s.sendStatus === 'SENT' ? 'success' : s.sendStatus === 'QUEUED' ? 'info' : 'danger'}>{SEND_STATUS_LABEL[s.sendStatus] ?? s.sendStatus}</Badge>}
      </div>
      <Line label="발송">{sent}</Line>
      <Line label="방법">{s.sendMethodCode ? SEND_METHOD_LABEL[s.sendMethodCode] ?? '' : ''}</Line>
      <Line label="받은 곳">{s.recipientName?.trim()}</Line>
      <Line label="입고희망일">{delivery}</Line>
      <Line label="품목">{s.itemSummary?.trim()}</Line>
      <Line label="열람">{mdhm(s.viewedDtm)}</Line>
      <Line label="기한">{mdhm(s.expiresDtm)}</Line>
      <Line label="마감">{s.closedReasonCode ? `${CLOSED_REASON_LABEL[s.closedReasonCode] ?? ''}${s.closedDtm ? ` · ${mdhm(s.closedDtm)}` : ''}` : ''}</Line>
    </li>
  )
}

function ReplyCard({ s }: { s: OrderSendHistoryItem }) {
  const accepted = s.replyResultCode === 'ACCEPTED'
  const elapsed = replyElapsedText(s.sentDtm, s.replyDtm)
  return (
    <li className="rounded-xl p-3 flex flex-col gap-1" style={cardStyle}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <b className="text-sm" style={{ color: 'var(--shell-on-surface)' }}>{SEND_TYPE_LABEL[s.sendTypeCode] ?? s.sendTypeCode}</b>
        {s.attemptNo != null && <span style={caption}>{`${s.attemptNo}차`}</span>}
        <Badge status={accepted ? 'success' : 'danger'}>{accepted ? ORDER_MESSAGE.replyAccepted : ORDER_MESSAGE.replyDeclined}</Badge>
        {s.sendMethodCode === 'OFFLINE' && <Badge status="mute">{SEND_METHOD_LABEL.OFFLINE}</Badge>}
      </div>
      <Line label="회신">{mdhm(s.replyDtm)}{elapsed ? ` · 발송 ${elapsed} 뒤` : ''}</Line>
      {!accepted && <Line label="사유">{s.replyReasonCode ? DECLINE_REASON_LABEL[s.replyReasonCode] ?? '' : ''}</Line>}
      <Line label="가능한 날짜">{md(s.availDate)}</Line>
      <Line label="양중">{s.liftAvailYn === 'Y' ? ORDER_MESSAGE.liftOk : s.liftAvailYn === 'N' ? ORDER_MESSAGE.liftNg : ''}</Line>
      <Line label="메모">{s.replyNote?.trim()}</Line>
    </li>
  )
}

const ITEM_COLUMNS: readonly TableColumn<OrderItemView>[] = [
  { key: 'itemName', header: '품목명', align: 'left' },
  { key: 'itemSpec', header: '규격', align: 'left', render: r => r.itemSpec ?? '' },
  { key: 'itemQty', header: '수량', align: 'right' },
  { key: 'itemUnit', header: '단위', align: 'center' },
]

export function MaterialStepDetailModal({ open, onClose, row, stepKey, workTypeName }: Props) {
  const usesSends = stepKey === 'order-request' || stepKey === 'order-reply' || stepKey === 'ship-request'
  const sendsQ = useOrderSends(row.orderVendorId, open && usesSends)
  const [photoKind, setPhotoKind] = useState<'LOAD' | 'UNLOAD' | null>(null)
  const [photoLoading, setPhotoLoading] = useState(false)
  const subtitle = [workTypeName, row.vendorName].filter(Boolean).join(' · ')

  const sends = sendsQ.data ?? []
  const orderSends = sends.filter(s => s.sendTypeCode === 'ORDER_REQUEST' || s.sendTypeCode === 'ORDER_CHANGE')
  const replySends = orderSends.filter(s => s.replyResultCode === 'ACCEPTED' || s.replyResultCode === 'DECLINED')
  const shipSends = sends.filter(s => s.sendTypeCode === 'SHIP_REQUEST')

  const renderSends = (list: OrderSendHistoryItem[], emptyTitle: string, card: (s: OrderSendHistoryItem) => ReactNode) => {
    if (sendsQ.isLoading) {
      return (
        <div aria-busy="true" className="mt-2 flex flex-col gap-2">
          {[0, 1, 2].map(i => <Skeleton key={i} height={28} />)}
        </div>
      )
    }
    if (sendsQ.isError) return <div className="mt-2"><ErrorState title="내역을 불러오지 못했어요" onRetry={() => { void sendsQ.refetch() }} /></div>
    if (list.length === 0) return <div className="mt-2"><EmptyState title={emptyTitle} /></div>
    return <ul className="mt-2 flex flex-col gap-2 list-none p-0 m-0">{list.map(card)}</ul>
  }

  const d = row.delivery
  const r = row.receipt
  const photoLink = (kind: 'LOAD' | 'UNLOAD', count: number | null | undefined) => {
    if (count == null || count <= 0) return null
    const label = kind === 'LOAD' ? '상차사진' : '하차사진'
    return (
      <TextLink onClick={() => setPhotoKind(kind)} disabled={photoLoading && photoKind === kind} aria-busy={photoLoading && photoKind === kind}>
        {photoLoading && photoKind === kind ? '사진 불러오는 중…' : `${label} ${count}장 ›`}
      </TextLink>
    )
  }

  return (
    <>
      <Modal open={open} onClose={() => { if (photoKind == null) onClose() }} title={TITLE[stepKey]} size="md">
        <div onMouseDown={e => e.stopPropagation()}>
          <div style={caption}>자재파트너 진행 내역</div>
          {subtitle && <div className="text-sm font-bold" style={{ color: 'var(--shell-on-surface)' }}>{subtitle}</div>}

          {stepKey === 'order-request' && (
            <>
              {renderSends(orderSends, '보낸 발주정보가 없어요', s => <SendCard key={s.orderSendId} s={s} />)}
              <div className="mt-3 text-xs font-semibold" style={caption}>현재 발주 내용</div>
              <div className="mt-1">
                <Table
                  columns={ITEM_COLUMNS}
                  rows={row.items}
                  rowKey={it => it.itemSeq}
                  empty={ORDER_MESSAGE.card.noItems}
                />
              </div>
              <div className="mt-2 flex flex-col gap-1">
                <Line label="양중">{row.liftReqYn === 'Y' ? ORDER_MESSAGE.card.liftYes : row.liftReqYn === 'N' ? ORDER_MESSAGE.card.liftNo : ''}</Line>
                <Line label="현장 주소">{row.deliveryAddress?.trim()}</Line>
                <Line label="남길 말">{row.orderNote?.trim()}</Line>
                <Line label="입고희망일">{md(row.deliveryDate)}</Line>
              </div>
            </>
          )}

          {stepKey === 'order-reply' && renderSends(replySends, '아직 회신이 없어요', s => <ReplyCard key={s.orderSendId} s={s} />)}

          {stepKey === 'ship-request' && renderSends(shipSends, '배송정보 요청 내역이 없어요', s => <SendCard key={s.orderSendId} s={s} />)}

          {stepKey === 'ship-reply' && (
            d && d.replyDtm ? (
              <div className="mt-2 rounded-xl p-3 flex flex-col gap-1" style={cardStyle}>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <b className="text-sm" style={{ color: 'var(--shell-on-surface)' }}>배송정보</b>
                  {d.replyRound != null && <span style={caption}>{`${d.replyRound}차 회신`}</span>}
                  {d.issueYn === 'Y' && <Badge status="danger">이슈 있음</Badge>}
                </div>
                <Line label="회신">{mdhm(d.replyDtm)}</Line>
                <Line label="도착">{[md(d.arrivalDate), d.arrivalTime ? d.arrivalTime.slice(0, 5) : ''].filter(Boolean).join(' ')}</Line>
                <Line label="이전 도착">{d.prevArrivalDate ? [md(d.prevArrivalDate), d.prevArrivalTime ? d.prevArrivalTime.slice(0, 5) : ''].filter(Boolean).join(' ') : ''}</Line>
                <Line label="기사">{d.driverName?.trim()}</Line>
                <Line label="기사 연락처">{d.driverPhone?.trim()}</Line>
                <Line label="이슈 메모">{d.issueNote?.trim()}</Line>
                <Line label="상차사진">{photoLink('LOAD', d.photoCnt)}</Line>
              </div>
            ) : <div className="mt-2"><EmptyState title="아직 배송정보 회신이 없어요" /></div>
          )}

          {stepKey === 'receipt' && (
            r ? (
              <div className="mt-2 rounded-xl p-3 flex flex-col gap-1" style={cardStyle}>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <b className="text-sm" style={{ color: 'var(--shell-on-surface)' }}>입고 내역</b>
                  {r.qtyCheckedCode && <Badge status={r.qtyCheckedCode === 'ISSUE' ? 'danger' : 'success'}>{QTY_CHECK_LABEL[r.qtyCheckedCode] ?? r.qtyCheckedCode}</Badge>}
                </div>
                <Line label="입고">{[md(r.receiptDate), mdhm(r.receiptDtm)].filter(Boolean).join(' · ')}</Line>
                <Line label="확인 방법">{r.receiptMethodCode ? RECEIPT_METHOD_LABEL[r.receiptMethodCode] ?? '' : ''}</Line>
                <Line label="메모">{r.issueNote?.trim()}</Line>
                <Line label="하차사진">{photoLink('UNLOAD', r.photoCnt)}</Line>
              </div>
            ) : <div className="mt-2"><EmptyState title="아직 입고 내역이 없어요" /></div>
          )}
        </div>
      </Modal>
      {photoKind && (
        <ShipPhotoViewerHost
          orderVendorId={row.orderVendorId}
          title={photoKind === 'LOAD' ? '상차사진' : '하차사진'}
          startKind={photoKind}
          onClose={() => { setPhotoKind(null); setPhotoLoading(false) }}
          onLoadingChange={setPhotoLoading}
        />
      )}
    </>
  )
}

